import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { X, Loader2, Download, Check } from "lucide-react";
import { API_BASE } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import AssuntosPicker from "@/components/AssuntosPicker";
import { expandAssuntos } from "@/data/assuntos";

export async function downloadChecklistPdf(slug) {
  const res = await fetch(`${API_BASE}/checklist/${slug}/pdf`, { credentials: "include" });
  if (!res.ok) throw new Error("Não foi possível gerar o PDF.");
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `guia-${slug}-maternnia.pdf`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

const inp = "w-full bg-white border border-maternnia-nevoa rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-maternnia-eucalipto/30 focus:border-maternnia-eucalipto transition";

const ChecklistDownloadModal = ({ open, onClose, slug, etapaNome }) => {
  const { refresh } = useAuth();
  const [form, setForm] = useState({ nome: "", email: "", senha: "", consent: false, assuntos: [], outrosTexto: "" });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleAssunto = (a) => setForm((f) => ({ ...f, assuntos: f.assuntos.includes(a) ? f.assuntos.filter((x) => x !== a) : [...f.assuntos, a] }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.consent) { toast.error("É preciso aceitar para baixar o checklist."); return; }
    setBusy(true);
    try {
      const res = await fetch(`${API_BASE}/checklist/download`, {
        method: "POST", credentials: "include", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ nome: form.nome, email: form.email, etapa: etapaNome, consent: form.consent, senha: form.senha || "", assuntos: expandAssuntos(form.assuntos, form.outrosTexto) }),
      });
      if (!res.ok) { const err = await res.json().catch(() => ({})); throw new Error(err.detail || "Erro ao processar."); }
      const data = await res.json();
      await downloadChecklistPdf(slug);
      if (data.account_created) { await refresh(); toast.success("Conta criada! Seu progresso será salvo."); }
      else toast.success("Checklist enviado! O download começou.");
      onClose();
      setForm({ nome: "", email: "", senha: "", consent: false, assuntos: [], outrosTexto: "" });
    } catch (err) {
      toast.error("Não foi possível baixar", { description: err.message });
    } finally { setBusy(false); }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div className="fixed inset-0 z-[100] flex items-center justify-center p-4"
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
          <div className="absolute inset-0 bg-maternnia-charcoal/50 backdrop-blur-sm" onClick={onClose} />
          <motion.div data-testid="checklist-download-modal"
            initial={{ opacity: 0, y: 30, scale: 0.97 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 20, scale: 0.98 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="relative bg-white rounded-[1.75rem] w-full max-w-md p-8 shadow-2xl">
            <button data-testid="checklist-modal-close" onClick={onClose} className="absolute top-5 right-5 text-maternnia-taupe hover:text-maternnia-charcoal"><X size={20} /></button>
            <div className="h-12 w-12 rounded-2xl bg-maternnia-eucalipto/10 flex items-center justify-center mb-4"><Download size={22} className="text-maternnia-eucalipto" /></div>
            <h3 className="font-serif text-2xl text-maternnia-charcoal font-medium">Baixar guia premium — {etapaNome}</h3>
            <p className="mt-2 text-sm text-maternnia-taupe font-light">Deixe seu contato para baixar o guia completo em PDF (checklist + dados oficiais de saúde) e receber conteúdos do seu momento.</p>
            <form onSubmit={submit} className="mt-5 space-y-3.5">
              <input data-testid="dl-nome" required placeholder="Seu nome" className={inp} value={form.nome} onChange={(e) => set("nome", e.target.value)} />
              <input data-testid="dl-email" required type="email" placeholder="E-mail" className={inp} value={form.email} onChange={(e) => set("email", e.target.value)} />
              <AssuntosPicker etapaNome={etapaNome} assuntos={form.assuntos} onToggle={toggleAssunto} outrosTexto={form.outrosTexto} onOutrosChange={(v) => set("outrosTexto", v)} inputClass={inp} label="Assuntos de interesse (opcional)" />
              <input data-testid="dl-senha" type="password" placeholder="Criar senha para salvar meu progresso (opcional)" className={inp} value={form.senha} onChange={(e) => set("senha", e.target.value)} />
              <label className="flex items-start gap-3 cursor-pointer group py-1">
                <input data-testid="dl-consent" type="checkbox" checked={form.consent} onChange={(e) => set("consent", e.target.checked)} className="sr-only" />
                <span className={`mt-0.5 h-5 w-5 rounded-md border flex items-center justify-center shrink-0 transition-all ${form.consent ? "bg-maternnia-eucalipto border-transparent" : "border-maternnia-nevoa"}`}>
                  {form.consent && <Check size={12} className="text-white" />}
                </span>
                <span className="text-xs text-maternnia-taupe font-light leading-relaxed">Aceito receber comunicações da Maternnia e concordo com a <a href="/privacidade" target="_blank" rel="noreferrer" className="text-maternnia-eucalipto underline">Política de Privacidade</a>.</span>
              </label>
              <button data-testid="dl-submit" type="submit" disabled={busy} className="w-full inline-flex items-center justify-center gap-2 bg-maternnia-eucalipto text-white rounded-full px-6 py-3.5 text-sm font-medium hover:bg-maternnia-floresta transition disabled:opacity-70">
                {busy ? <Loader2 size={18} className="animate-spin" /> : (<><Download size={16} /> Baixar guia premium</>)}
              </button>
            </form>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default ChecklistDownloadModal;
